<template>
    <div class="card mx-auto">
        <!-- Spinner -->
        <loading :active.sync="loading">
        </loading>
        <!-- /Spinner -->

        <!-- Card-header -->
        <div class="card-header">
            <h3>User List</h3>
        </div>
        <!-- /Card-header -->

        <!-- Card body -->
        <div class="card-body">
            <table class="table table-bordered table-striped table-hover">
                <colgroup>
                    <col v-if="hasAnyRole('Supervisor')" width="5%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="10%" />
                    <col width="5%" />
                    <col width="5%" />
                </colgroup>
                <thead>
                    <tr class="text-center">
                        <th v-if="hasAnyRole('Supervisor')">Tenant</th>
                        <th>User ID</th>
                        <th>Name</th>
                        <th>Roles</th>
                        <th>가입 승인</th>
                        <th>삭제</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="user in userList" :key="user.id">
                        <td v-if="hasAnyRole('Supervisor')" class="text-center">{{ user.tenantId }}</td>
                        <td>{{ user.userId }}</td>
                        <td>{{ user.userName }}</td>
                        <td>
                            <b-container>
                                <b-row align-h="around">
                                    <b-col class="d-flex align-item-center justify-content-start">
                                        <b-form-checkbox-group id="userlist-checkbox-group" name="roles"
                                                               v-model="user.roleCode" :options="roleCodes"
                                                               button-variant="outline-dark" buttons size="sm"
                                                               @change="showUpdateRole(user)"></b-form-checkbox-group>
                                    </b-col>
                                    <b-col class="d-flex align-item-center justify-content-start">
                                        <label class="btn btn-sm btn-primary my-1" :class="{ disabled: !user.updatable }"
                                               @click="updateRole($event, user)">Update</label>
                                    </b-col>
                                </b-row>
                            </b-container>
                        </td>
                        <td class="text-center">
                            <label v-if="user.status === 0" class="btn btn-sm btn-success mb-0"
                                   @click="confirmUser(user)">Confirm</label>
                        </td>
                        <td class="text-center">
                            <label class="btn btn-sm btn-danger mb-0" @click="deleteUser(user)">Delete</label>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- /Card body -->
    </div>
    <!--/card -->
</template>

<script>
import Loading from 'vue-loading-overlay';
import accessToken from '../services/accessToken';

export default {
    name: 'UserList',
    components: { Loading },
    async mounted() {
        await this.getUserList();
    },
    computed: {
        currentUser() {
            return this.$store.getters['auth/currentUser'];
        }
    },
    data() {
        return {
            userList: null,
            loading: false,
            roleCodes: [
                { value: 'Administrator', text: 'Admin' },
                { value: 'Operator', text: 'Operator' },
                { value: 'Surveyor', text: 'Surveyor' },
                { value: 'Chatterer', text: 'Chatterer' },
                { value: 'ESG', text: 'ESG' },
            ]
        }
    },
    methods: {
        showError(error) {
            if (error.status != 401) {
                this.$store.commit('setMessage', error.message);
            }
        },
        async getUserList() {
            try {
                this.loading = true;

                // Fetch user list
                await this.$store.dispatch('retrieveUserList');

                // Clone the list
                let cloned = JSON.parse(JSON.stringify(this.$store.getters.getUserList));

                // Add 'updatable' field
                cloned.forEach(user => user.updatable = false);

                // Set userList to the cloned data
                this.userList = cloned;
            } catch (error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        showUpdateRole(user) {
            // Find original user info 
            let original = this.$store.getters.getUserList.filter(item => item.userId === user.userId);

            if (original.length !== 1) {
                this.showMessage(`유저가 존재하지 않습니다. (ID: ${user.userId})`);
                user.updatable = false;
            }

            // Compare old and new roles
            let oldRoles = original[0].roleCode;
            let newRoles = user.roleCode;

            // If they are not equal, show the Update Role button
            user.updatable = !(oldRoles.length === newRoles.length && newRoles.every(newRole => oldRoles.includes(newRole)));
        },
        async confirmUser(user) {
            await this.$store.dispatch('confirmUser', user);
            this.getUserList();
        },
        async deleteUser(user) {
            try {
                // Delete user
                this.loading = true;
                await this.$store.dispatch('deleteUser', user);

                // Retrieve user
                await this.getUserList();
                this.loading = false;

                // Show message
                this.showMessage('정상적으로 삭제 되었습니다.');
            } catch (error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        async updateRole(event, user) {
            // If update button is diabled, return 
            if (event.target.classList.contains("disabled")) return;

            // Update confirmation
            let confirmed = false;

            try {
                // Check if the user exists
                this.loading = true;
                await axios.get('user/retrieveUserInfo', {
                    params: {
                        'tenantId': user.tenantId,
                        'userId': user.userId
                    },
                    headers: {
                        Authorization: accessToken()
                    }
                });
                this.loading = false;

                // Get confirmed
                let confirmMsg = '권한을 다음과 같이 변경하시겠습니까? ' + user.roleCode.join(', ');
                confirmed = await this.showConfirm(confirmMsg);

                // Return if not confirmed
                if (!confirmed) return

                // Update user info
                this.loading = true;
                await axios.post('user/userModify',
                    {
                        'tenantId': user.tenantId,
                        'userId': user.userId,
                        'roleCode': user.roleCode
                    },
                    {
                        headers: {
                            Authorization: accessToken()
                        }
                    }
                );
                this.loading = false;
                await this.showMessage('정상적으로 변경되었습니다.');

                // Update user list
                this.loading = true;
                await this.getUserList();
                this.loading = false;

                // Log out if the current user's info has been updated
                if (user.userId === this.currentUser.caller) {
                    await this.showMessage('권한변경을 반영하기 위해 로그아웃합니다.')
                    this.pushRoute('/signin');
                    this.$store.dispatch('auth/logout');
                }
            } catch (error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
    },
}
</script>

<style>
#userlist-checkbox-group label {
    display: flex;
    align-items: center;
}
</style>

<style scoped>
table.table thead tr th,
table.table tbody tr td {
    vertical-align: middle;
}

label.disabled {
    background-color: #888;
    border: #888;
}

label.disabled:hover {
    cursor: default;
}
</style>
